import type {
  FormSchemaDef,
  FormAnswers,
  AnswerMap,
  SelectOption,
  ConditionalSelectDef,
} from '@/components/common/form/types';
import type { DiagnosticDataEntry } from '@/types/proposal';

// TODO: この関数は UI コンポーネントではなくドメインロジック。
// components/ 配下にあるのはコロケーション目的だが、将来的には features/ や lib/ などに移動を検討。

/** options から id に対応するラベルを引く。見つからなければ id をそのまま返す */
function findLabel(options: readonly SelectOption[], id: string): string {
  return options.find((o) => o.id === id)?.label ?? id;
}

/**
 * 診断フォームの回答をバックエンドAPI用のペイロードに変換する純粋関数
 */
export function buildDiagnosticPayload(
  schema: FormSchemaDef,
  answers: FormAnswers
): DiagnosticDataEntry[] {
  const entries: DiagnosticDataEntry[] = [];

  for (const step of schema.steps) {
    for (const question of step.questions) {
      const answer = answers[question.id];
      if (!answer) continue;

      switch (question.type) {
        case 'SINGLE_SELECT': {
          const a = answer as AnswerMap['SINGLE_SELECT'];
          entries.push({
            question: question.label,
            answer: findLabel(question.options, a.selectedId),
          });
          break;
        }
        case 'MULTI_SELECT': {
          const a = answer as AnswerMap['MULTI_SELECT'];
          entries.push({
            question: question.label,
            answer: a.selectedIds.map((id) => findLabel(question.options, id)).join(', '),
          });
          break;
        }
        case 'CONDITIONAL_SELECT': {
          const a = answer as AnswerMap['CONDITIONAL_SELECT'];
          entries.push(...buildConditionalEntries(question, a.selections, a.secondarySelections));
          break;
        }
      }
    }
  }

  return entries;
}

/** 条件付き選択: アイテムごとにプライマリ + セカンダリを結合した entry を生成 */
function buildConditionalEntries(
  question: ConditionalSelectDef,
  selections: Record<string, string>,
  secondarySelections: Record<string, Record<string, string>>
): DiagnosticDataEntry[] {
  return question.items.flatMap((item) => {
    const primaryId = selections[item.id];
    if (!primaryId) return [];

    const primaryLabel = findLabel(question.primaryOptions, primaryId);
    const secondaryLabel = resolveSecondaryLabel(question, item.id, primaryId, secondarySelections);

    return [{
      question: item.label,
      answer: secondaryLabel ? `${primaryLabel}, ${secondaryLabel}` : primaryLabel,
    }];
  });
}

/** トリガー条件に合致する場合、セカンダリ選択のラベルを返す */
function resolveSecondaryLabel(
  question: ConditionalSelectDef,
  itemId: string,
  primaryId: string,
  secondarySelections: Record<string, Record<string, string>>
): string | null {
  const rule = question.conditionalRules.find((r) => r.triggerValue === primaryId);
  if (!rule) return null;

  const secondaryId = secondarySelections[itemId]?.[primaryId];
  if (!secondaryId) return null;

  return findLabel(rule.secondaryOptions, secondaryId);
}
